import React, { useState } from "react";
import { Sheet, Button, Field, useInputStyle, useMoney, todayIso } from "../ui";
import { useTheme } from "../../theme/ThemeContext";
import { bankAccountsOf, sourceRequired, paidFromFields, receivedIntoId } from "../../utils/moneySources";
import MoneySourceSelect from "../../components/MoneySourceSelect";

// Settling money between you and someone else:
//   kind "lent":     money you lent (a "Lent" expense) came back - into which account?
//   kind "borrowed": money you borrowed ("Borrowed" income) paid back - from where?
// `entry` is the bill (lent) or income row (borrowed).
const SettleSheet = ({ kind, entry, data, onClose }) => {
  const { tokens } = useTheme();
  const input = useInputStyle();
  const money = useMoney();
  const lent = kind === "lent";
  const who = lent ? entry.payee : entry.source;
  const [date, setDate] = useState(todayIso());
  const [source, setSource] = useState("");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const hasChoice = lent ? bankAccountsOf(data.assets).length > 0 : bankAccountsOf(data.assets).length > 0 || data.cards.length > 0;
  const required = hasChoice && sourceRequired(date, data.assets, data.cards);

  const submit = async () => {
    if (required && !source) return setError(lent ? "Choose the account it came into." : "Choose where you paid it from.");
    setSaving(true);
    setError("");
    try {
      if (lent) await data.markRepaid(entry.id, { date, receivedIntoId: receivedIntoId(source) });
      else await data.repayBorrowed(entry.id, { date, ...paidFromFields(source) });
      onClose();
    } catch (err) {
      setError(err.message);
      setSaving(false);
    }
  };

  return (
    <Sheet title={lent ? `${who} paid you back` : `Pay back ${who}`} onClose={onClose}>
      <p style={{ margin: "0 0 14px", fontSize: 14, color: tokens.textPrimary }}>
        {money(entry.amount)} {lent ? "you lent" : "you borrowed"} {lent ? "is added to your money received" : "is recorded as paid back (not counted as spending)"}.
      </p>
      <Field label={lent ? "Got it back on" : "Paid back on"}>
        <input style={input} type="date" value={date} max={todayIso()} onChange={(e) => setDate(e.target.value)} />
      </Field>
      {hasChoice && (
        <Field label={lent ? "Received into" : "Paid from"}>
          <MoneySourceSelect style={input} value={source} onChange={setSource} assets={data.assets} cards={data.cards} accountsOnly={lent} required={required} />
        </Field>
      )}
      {error && <div role="alert" style={{ marginBottom: 10, fontSize: 13, color: tokens.negative }}>{error}</div>}
      <Button disabled={saving} onClick={submit} style={{ width: "100%" }}>{saving ? "Saving…" : lent ? "Got it back" : "Paid it back"}</Button>
    </Sheet>
  );
};

export default SettleSheet;
